import { useIsFetching } from '@zg-rentals/react-query5';
import { useEffect, useState } from 'react';

import { ReviewRenderer } from '@zillow/cosmos';

import {
  getCosmosJourneyTokenFromOuid,
  useInfoForUser,
  useLeaseExtensionFromOuidQuery,
  useLeaseSummaryFromOuidQuery,
} from '../../utils/queries';
import NoAccessLeaseRenewalAlert from '../../components/Widgets/NoAccessLeaseRenewalAlert';
import LeaseActionButtonGroup from '../../components/LeaseActions/LeaseActionButtonGroup';
import type { LeaseAction } from '../../models';
import LeaseExtension from '../../models/LeaseExtension';
import { Box, Card, Paragraph, Tag } from '@zillow/constellation-10';
import { LoadingButton, LoadingHeading, LoadingText } from '@zg-rentals/loading-components';
import ColandlordFaq from '../../components/Containers/ColandlordContentContainer/ColandlordFaq';
import ParticipantContainer from '../../components/Containers/ParticipantContainer';
import { LeaseStatus } from '@zg-rentals/ts-rental-manager-api-client';
import WhoHasSigned from '../../components/Widgets/WhoHasSigned';
import SummaryTitle from '../../components/LeaseSummary/SummaryTitle';

export function ColandlordSummary() {
  const queryParameters = new URLSearchParams(window.location.search);
  const ouid = queryParameters.get('ouid');
  const leaseSummaryFromOuidQuery = useLeaseSummaryFromOuidQuery(ouid, !!ouid);
  const leaseExtensionFromOuidQuery = useLeaseExtensionFromOuidQuery(ouid, !!ouid);
  const infoForUserQuery = useInfoForUser(ouid, !!ouid);

  const [isReviewRendererLoading, setIsReviewRendererLoading] = useState(false);
  const [hasRenderedReviewRenderer, setHasRenderedReviewRenderer] = useState(false);

  const leaseExtension = new LeaseExtension(leaseExtensionFromOuidQuery?.data);

  const isFetching = useIsFetching();

  // Track when we're rendering a ReviewRenderer so we know to wait for it to load
  useEffect(() => {
    if (leaseExtension?.journeyId) {
      setHasRenderedReviewRenderer(true);
      setIsReviewRendererLoading(true);
    }
  }, [leaseExtension?.journeyId]);

  function generateJourneyToken() {
    return getCosmosJourneyTokenFromOuid(ouid);
  }

  const { createdTime, updatedTime, startDate, sharedDate } = leaseExtension;

  const statusExtraMap = {
    [LeaseStatus.INCOMPLETE_DRAFT]: createdTime ? (
      <Paragraph css={{ textStyle: 'body-sm', marginBottom: 'tight' }}>
        Creation started on {leaseExtension.getFriendlyCreationDate()}
      </Paragraph>
    ) : null,
    [LeaseStatus.COMPLETE_DRAFT]: updatedTime ? (
      <Paragraph css={{ textStyle: 'body-sm', marginBottom: 'tight' }}>
        Completed on {leaseExtension.getFriendlyUpdatedDate()}
      </Paragraph>
    ) : null,
    [LeaseStatus.SENT_COMPLETE_DRAFT]: updatedTime ? (
      <Paragraph css={{ textStyle: 'body-sm', marginBottom: 'tight' }}>
        Last edited on {leaseExtension.getFriendlyUpdatedDate()}
      </Paragraph>
    ) : null,
    [LeaseStatus.SENT_INCOMPLETE_DRAFT]: updatedTime ? (
      <Paragraph css={{ textStyle: 'body-sm', marginBottom: 'tight' }}>
        Last edited on {leaseExtension.getFriendlyUpdatedDate()}
      </Paragraph>
    ) : null,
    [LeaseStatus.SENT_FOR_ONLINE_SIGNING]: <></>,
    [LeaseStatus.SIGNING_CANCELLED]: <></>,
    [LeaseStatus.SIGNING_COMPLETE]:
      updatedTime && startDate ? (
        <Paragraph css={{ textStyle: 'body-sm', marginBottom: 'tight' }}>
          Lease renewal agreement becomes active on {leaseExtension.getFriendlyStartDate()}
        </Paragraph>
      ) : null,
    [LeaseStatus.ACTIVE]: updatedTime ? (
      <Paragraph css={{ textStyle: 'body-sm', marginBottom: 'tight' }}>
        Lease signed on {leaseExtension.getFriendlyUpdatedDate()}
      </Paragraph>
    ) : null,
    [LeaseStatus.EXPIRED]: updatedTime ? (
      <Paragraph css={{ textStyle: 'body-sm', marginBottom: 'tight' }}>
        Expired {leaseExtension.getFriendlyUpdatedDate()}
      </Paragraph>
    ) : null,
    [LeaseStatus.SIGNED_BY_TENANT]: (
      <>
        {sharedDate && (
          <Paragraph css={{ textStyle: 'body-sm', marginBottom: 'tight' }}>
            The landlord requested signatures on {leaseExtension.getFriendlySharedDate()}
          </Paragraph>
        )}
      </>
    ),
    [LeaseStatus.COUNTERSIGNING]: <></>,
  };

  const showSkeleton =
    !leaseExtension?.journeyId || !hasRenderedReviewRenderer || isReviewRendererLoading || isFetching;

  const skeleton = (
    <>
      <LoadingHeading
        isLoading
        level={2}
        loadingWidth="60%"
        css={{
          textStyle:
            'responsive.heading-xs' /* eslint-disable-line c11nLint/no-unknown-token -- valid textStyle; rule can't check textStyle (JSINFRA-3580) */,
          marginBottom: 'tighter',
        }}
      >
        Loading
      </LoadingHeading>
      <LoadingText isLoading loadingWidth="40%" css={{ marginBottom: 'tight' }}>
        Loading
      </LoadingText>
      <LoadingButton isLoading css={{ marginBottom: 'tight' }}>
        Action
      </LoadingButton>
      {[1, 2, 3].map((index) => (
        <Card key={index} tone="neutral" elevated={false} css={{ marginBottom: 'tighter' }}>
          <LoadingHeading
            isLoading
            level={3}
            loadingWidth="40%"
            css={{
              marginBottom: 'loose',
              textStyle:
                'responsive.heading-xs' /* eslint-disable-line c11nLint/no-unknown-token -- valid textStyle; rule can't check textStyle (JSINFRA-3580) */,
            }}
          >
            Loading
          </LoadingHeading>
          {[1, 2, 3].map((rowIndex) => (
            <Box key={rowIndex} css={{ display: 'grid', gridTemplateColumns: 'repeat(12, 1fr)', gap: 'tighter' }}>
              <Box css={{ gridColumnEnd: { base: 'span 12', md: 'span 4' } }}>
                <LoadingText isLoading loadingWidth="50%">
                  Loading
                </LoadingText>
              </Box>
              <Box css={{ gridColumnEnd: { base: 'span 12', md: 'span 8' } }}>
                <LoadingText isLoading loadingWidth="70%">
                  Loading
                </LoadingText>
                <LoadingText isLoading loadingWidth="40%" css={{ marginBottom: 'tighter' }}>
                  Loading
                </LoadingText>
                <LoadingText isLoading loadingWidth="60%">
                  Loading
                </LoadingText>
              </Box>
            </Box>
          ))}
        </Card>
      ))}
      <LoadingButton isLoading css={{ marginTop: 'tight' }}>
        Action
      </LoadingButton>
    </>
  );

  return (
    <ParticipantContainer
      rightSidebarContent={
        <>
          <Box>
            {leaseExtension.getHumanReadableStatus() ? (
              <Tag tone={leaseExtension.getBadgeColor()} css={{ marginBottom: 'tight', textTransform: 'capitalize' }}>
                {leaseExtension.getHumanReadableStatus()}
              </Tag>
            ) : (
              <LoadingText isLoading loadingWidth="10rem">
                Loading
              </LoadingText>
            )}
            {statusExtraMap[leaseExtension.status]}
          </Box>
          <WhoHasSigned usesOuid isInitiallyExpanded />
          <ColandlordFaq isLeaseRenewal />
        </>
      }
    >
      <>
        {(!ouid || leaseSummaryFromOuidQuery.isError || leaseExtensionFromOuidQuery.isError) && (
          <NoAccessLeaseRenewalAlert />
        )}

        {showSkeleton ? (
          skeleton
        ) : (
          <>
            <SummaryTitle
              {...{
                summaryText: leaseSummaryFromOuidQuery?.data?.summaryText,
                propertyAddress: infoForUserQuery?.data?.address,
                personFirstName: infoForUserQuery?.data?.firstName,
                leaseStartDate: leaseExtension.getFriendlyStartDate(),
                isColandlord: true,
              }}
            />
            {leaseSummaryFromOuidQuery?.data && (
              <>
                <LeaseActionButtonGroup
                  leaseActions={leaseSummaryFromOuidQuery?.data?.summaryActions as Array<LeaseAction>}
                  isTop={true}
                  fluid
                />
                <Box css={{ marginTop: 'tight' }} />
              </>
            )}
          </>
        )}

        <Box css={{ visibility: showSkeleton ? 'hidden' : 'visible', height: showSkeleton ? '0' : 'auto' }}>
          {leaseExtension && leaseExtension?.journeyId && (
            <ReviewRenderer
              journeyId={leaseExtension.journeyId}
              journeyVersionId={leaseExtension.journeyVersionId}
              generateJourneyToken={generateJourneyToken}
              isReadOnly
              setIsReviewRendererLoading={setIsReviewRendererLoading}
            />
          )}
          {leaseSummaryFromOuidQuery?.data && !showSkeleton && (
            <>
              <Box css={{ marginTop: 'tight' }} />
              <LeaseActionButtonGroup
                leaseActions={leaseSummaryFromOuidQuery?.data?.summaryActions as Array<LeaseAction>}
                isTop={false}
                fluid
              />
            </>
          )}
        </Box>
      </>
    </ParticipantContainer>
  );
}
